import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    FormControlLabel,
    Grow,
    IconButton,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useHelp } from '../context/HelpProvider';
import { LoomEmbed } from './HelpContent';
import helpService from '../services/HelpService';

// Articles attached to this anchor in Help Center Management drive the modal.
const WELCOME_ANCHOR = 'welcome.intro';

// Brand accent for the welcome moment.
const ACCENT = '#F26522';
const ACCENT_HOVER = '#e05a1c';

// Dismissal is permanent per user AND per video: swapping the Loom video in the
// admin panel produces a new key, so the refreshed intro is shown again even to
// users who previously ticked "don't show this again".
const dismissKey = (user, videoId) => `netbot:welcomeVideo:dismissed:${user}:${videoId}`;

// Scale + fade entrance so the dialog reads as a focused moment (modal-motion).
const GrowTransition = forwardRef(function GrowTransition(props, ref) {
    return <Grow ref={ref} timeout={260} {...props} />;
});

const normLang = (i18n) => {
    const l = String(i18n?.language || 'en').toLowerCase();
    return l.startsWith('ja') || l.startsWith('jp') ? 'jp' : 'en';
};

const readDismissed = (user, videoId) => {
    try {
        return localStorage.getItem(dismissKey(user, videoId)) === '1';
    } catch {
        return false;
    }
};

const groupIncludes = (group, expected) => {
    const wanted = String(expected ?? '').trim().toLowerCase();
    if (!wanted) return false;
    if (Array.isArray(group)) {
        return group.some((g) => String(g ?? '').trim().toLowerCase() === wanted);
    }
    return String(group ?? '').split(',')
        .some((g) => g.trim().toLowerCase() === wanted);
};

/**
 * Post-login welcome intro-video modal.
 *
 * Rendered once at the app root (outside <Routes>) so it appears immediately
 * after login regardless of the landing page. It is shown only in the customer
 * view (real customers or an admin impersonating one) and only when an admin
 * has published a Loom video against the `welcome.intro` anchor. Everything
 * about the video — its presence, URL, title, description and language — is
 * controlled from Help Center Management, not from code.
 */
export default function WelcomeVideoModal({ group, loginAs, userName }) {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { getForAnchor } = useHelp();
    const [open, setOpen] = useState(false);
    const [dontShow, setDontShow] = useState(false);
    const [welcomeArticles, setWelcomeArticles] = useState([]);

    // Customer-side users see the welcome video; bare admin users do not.
    const isCustomerView = groupIncludes(group, 'customer') || groupIncludes(group, 'member') || Boolean(loginAs);
    const effectiveUser = String(loginAs || userName || '').trim();

    const providerArticles = useMemo(() => {
        if (!isCustomerView) return null;
        return getForAnchor(WELCOME_ANCHOR) || [];
    }, [getForAnchor, isCustomerView]);

    useEffect(() => {
        if (!isCustomerView || !effectiveUser || (providerArticles && providerArticles.length)) {
            setWelcomeArticles([]);
            return undefined;
        }

        let cancelled = false;
        helpService.getArticlesForAnchor(WELCOME_ANCHOR, loginAs)
            .then((data) => {
                if (!cancelled) setWelcomeArticles(Array.isArray(data) ? data : []);
            })
            .catch((e) => {
                console.error('Failed to load welcome video article', e);
                if (!cancelled) setWelcomeArticles([]);
            });

        return () => { cancelled = true; };
    }, [effectiveUser, isCustomerView, loginAs, providerArticles]);

    // Pick the intro article for the current UI language, falling back to any
    // published one so a single-language upload still works.
    const article = useMemo(() => {
        if (!isCustomerView) return null;
        const list = (providerArticles && providerArticles.length) ? providerArticles : welcomeArticles;
        if (!list.length) return null;
        const lang = normLang(i18n);
        return list.find((a) => String(a.language || '').toLowerCase() === lang) || list[0];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCustomerView, i18n.language, providerArticles, welcomeArticles]);

    const videoId = article?.loomVideoId || '';

    useEffect(() => {
        if (!isCustomerView || !videoId || !effectiveUser) {
            setOpen(false);
            return;
        }
        setDontShow(false);
        setOpen(!readDismissed(effectiveUser, videoId));
    }, [isCustomerView, videoId, effectiveUser]);

    const handleClose = () => {
        if (dontShow && effectiveUser && videoId) {
            try {
                localStorage.setItem(dismissKey(effectiveUser, videoId), '1');
            } catch {
                /* localStorage unavailable — dismissal simply won't persist */
            }
        }
        setOpen(false);
    };

    if (!open || !article) return null;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth={false}
            aria-labelledby="welcome-video-title"
            aria-describedby="welcome-video-subtitle"
            TransitionComponent={GrowTransition}
            PaperProps={{
                sx: {
                    position: 'relative',
                    width: '100%',
                    maxWidth: { xs: 'calc(100% - 32px)', sm: 740 },
                    borderRadius: '14px',
                    overflow: 'hidden',
                    m: { xs: 2, sm: 3 },
                },
            }}
        >
            <IconButton
                aria-label={t('welcome.close')}
                onClick={handleClose}
                size="small"
                sx={{
                    position: 'absolute',
                    top: 14,
                    right: 14,
                    zIndex: 1,
                    color: 'text.secondary',
                    '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
                }}
            >
                <CloseIcon fontSize="small" />
            </IconButton>

            <Box sx={{ px: { xs: 2.5, sm: 3.5 }, pt: { xs: 3, sm: 3.5 }, pb: { xs: 2.5, sm: 3 } }}>
                {/* Header */}
                <Box sx={{ pr: 4, mb: 2 }}>
                    <Typography
                        id="welcome-video-title"
                        component="h2"
                        sx={{
                            fontSize: { xs: '1.25rem', sm: '1.35rem' },
                            fontWeight: 700,
                            color: 'text.primary',
                            lineHeight: 1.3,
                        }}
                    >
                        {t('welcome.title')}
                    </Typography>
                    <Typography
                        id="welcome-video-subtitle"
                        sx={{ mt: 0.5, fontSize: '0.875rem', color: 'text.secondary', lineHeight: 1.5 }}
                    >
                        {article.summary || t('welcome.subtitle')}
                    </Typography>
                </Box>

                {/* Video (16:9, space reserved by LoomEmbed to avoid layout shift) */}
                <LoomEmbed videoId={videoId} title={article.title || t('welcome.videoTitle')} />

                {/* Actions */}
                <Box
                    sx={{
                        mt: 3,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                    }}
                >
                    <FormControlLabel
                        sx={{ m: 0 }}
                        control={
                            <Checkbox
                                size="small"
                                checked={dontShow}
                                onChange={(e) => setDontShow(e.target.checked)}
                                sx={{ p: 0.5, mr: 0.75, '&.Mui-checked': { color: ACCENT } }}
                            />
                        }
                        label={
                            <Typography sx={{ fontSize: '0.825rem', color: 'text.secondary', userSelect: 'none' }}>
                                {t('welcome.dontShowAgain')}
                            </Typography>
                        }
                    />

                    <Button
                        onClick={() => { handleClose(); navigate('/help'); }}
                        variant="contained"
                        startIcon={<PlayArrowRoundedIcon />}
                        sx={{
                            flexShrink: 0,
                            minHeight: 40,
                            px: 2.5,
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            borderRadius: '8px',
                            textTransform: 'none',
                            background: ACCENT,
                            color: '#fff',
                            boxShadow: 'none',
                            transition: 'background-color 0.15s ease',
                            '&:hover': {
                                background: ACCENT_HOVER,
                                boxShadow: 'none',
                            },
                        }}
                    >
                        {t('welcome.exploreTutorials')}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
}
